// Provably-fair recompute — a byte-exact port of hub-core core/fairness.py.
// This is the entire trust story: the browser re-derives every revealed spin and
// checks it against the commit the server showed BEFORE the bet. If this diverges
// from Python by a single byte, "provably fair" becomes a lie. It runs on WebCrypto
// (crypto.subtle), which exists in both the browser and Node 20+ (so the dev mock and
// the cross-check use the very same code path).

import type { Mode, SpinOutcome, WeightTable } from './types';

const TOTAL_WEIGHT_PPM = 1_000_000n;
const DEFAULT_PITY_THRESHOLD = 100;
const DEFAULT_FS_WIN_MIN = 5;
const DEFAULT_FS_WIN_MAX = 25;

const encoder = new TextEncoder();

// WebCrypto wants ArrayBuffer-backed views (TS 5.7 typed-array generics distinguish these
// from SharedArrayBuffer-backed ones). Every producer below returns Uint8Array<ArrayBuffer>.
function utf8(s: string): Uint8Array<ArrayBuffer> {
  return new Uint8Array(encoder.encode(s));
}

export function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  if (hex.length % 2 !== 0) throw new Error('hex string must have even length');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

function concatBytes(a: Uint8Array, b: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

async function sha256(bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return new Uint8Array(digest);
}

async function hmacSha256(
  keyBytes: Uint8Array<ArrayBuffer>,
  msgBytes: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, msgBytes);
  return new Uint8Array(sig);
}

// digest = HMAC_SHA256(key = bytes.fromhex(server_seed), msg = "{client_seed}:{nonce}")
export async function hmacDigest(
  serverSeedHex: string,
  clientSeed: string,
  nonce: number,
): Promise<string> {
  return bytesToHex(await hmacSha256(hexToBytes(serverSeedHex), utf8(`${clientSeed}:${nonce}`)));
}

// commit = SHA256(bytes.fromhex(server_seed) ++ ascii(weight_table_hash))
export async function commitHash(serverSeedHex: string, weightTableHash: string): Promise<string> {
  const bytes = concatBytes(hexToBytes(serverSeedHex), utf8(weightTableHash));
  return bytesToHex(await sha256(bytes));
}

// raw_u = int(digest[:8], 16) / 2**32   (IEEE754 double, identical to Python float division)
export function rawUFromDigest(digest: string): number {
  return parseInt(digest.slice(0, 8), 16) / 2 ** 32;
}

// draw_ppm = (int(digest[:8], 16) * 1_000_000) // 2**32   (integer floor — BigInt to be exact)
function drawPpmFromDigest(digest: string): bigint {
  const u = BigInt('0x' + digest.slice(0, 8));
  return (u * TOTAL_WEIGHT_PPM) / (2n ** 32n);
}

// Canonical JSON == Python json.dumps(table, sort_keys=True, separators=(",",":")).
// Keys sorted recursively; no whitespace; non-ASCII escaped to \uXXXX (ensure_ascii=True).
export function canonicalJson(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('cannot canonicalize non-finite number');
    return JSON.stringify(value);
  }
  if (typeof value === 'string') return encodeJsonString(value);
  if (Array.isArray(value)) return '[' + value.map(canonicalJson).join(',') + ']';
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return '{' + keys.map((k) => encodeJsonString(k) + ':' + canonicalJson(obj[k])).join(',') + '}';
  }
  throw new Error(`cannot canonicalize value of type ${typeof value}`);
}

function encodeJsonString(s: string): string {
  let out = '"';
  for (const ch of s) {
    const code = ch.codePointAt(0)!;
    if (ch === '"') out += '\\"';
    else if (ch === '\\') out += '\\\\';
    else if (ch === '\n') out += '\\n';
    else if (ch === '\r') out += '\\r';
    else if (ch === '\t') out += '\\t';
    else if (ch === '\b') out += '\\b';
    else if (ch === '\f') out += '\\f';
    else if (code < 0x20) out += '\\u' + code.toString(16).padStart(4, '0');
    else if (code < 0x7f) out += ch;
    else out += asciiEscape(code); // ensure_ascii=True
  }
  return out + '"';
}

function asciiEscape(code: number): string {
  if (code <= 0xffff) return '\\u' + code.toString(16).padStart(4, '0');
  // Surrogate pair, matching Python's \uXXXX\uXXXX for astral code points.
  const c = code - 0x10000;
  const hi = 0xd800 + (c >> 10);
  const lo = 0xdc00 + (c & 0x3ff);
  return '\\u' + hi.toString(16).padStart(4, '0') + '\\u' + lo.toString(16).padStart(4, '0');
}

// Hash the EXACT canonical bytes hub-core provides (config.weight_table_json). This is the
// only correct way to reproduce weight_table_hash: re-serializing the browser-parsed table
// would diverge on any integer-valued float param (JS renders 1, Python "1.0").
export async function weightTableHashFromJson(tableJson: string): Promise<string> {
  return bytesToHex(await sha256(utf8(tableJson)));
}

// canonicalJson stays for the dev mock (which mints the canonical bytes it serves) and for
// tests. It is NOT used to verify server-issued spins — those use weightTableHashFromJson.
export async function weightTableHash(table: WeightTable): Promise<string> {
  return bytesToHex(await sha256(utf8(canonicalJson(table))));
}

// The deterministic outcome for a single spin. Mirrors resolve_outcome() including the
// pity override: a paid spin at the pity threshold is FORCED to jackpot and skips the
// draw, so a verifier that ignored pity would wrongly reject those spins.
export async function resolveOutcome(args: {
  serverSeedHex: string;
  clientSeed: string;
  nonce: number;
  weightTable: WeightTable;
  mode: Mode;
  pityAtSettle?: number;
}): Promise<{ rawU: number; prizeTier: string; prizeFs: number; forcedByPity: boolean }> {
  const { serverSeedHex, clientSeed, nonce, weightTable, mode } = args;
  const pityAtSettle = args.pityAtSettle ?? 0;
  const digest = await hmacDigest(serverSeedHex, clientSeed, nonce);
  const rawU = rawUFromDigest(digest);

  const params = weightTable.params ?? ({} as WeightTable['params']);
  const entries = weightTable.tables[mode];
  if (!entries) throw new Error(`weight table has no "${mode}" mode`);
  const pityThreshold = intOr(params.pity_threshold, DEFAULT_PITY_THRESHOLD);
  const tableHasJackpot = entries.some((e) => e.tier === 'jackpot');
  if (mode === 'paid' && pityThreshold > 0 && tableHasJackpot && pityAtSettle + 1 >= pityThreshold) {
    return { rawU, prizeTier: 'jackpot', prizeFs: 0, forcedByPity: true };
  }

  const drawPpm = drawPpmFromDigest(digest);
  let cursor = 0n;
  let selected = entries[entries.length - 1];
  for (const entry of entries) {
    cursor += BigInt(Math.trunc(entry.weight));
    if (drawPpm < cursor) {
      selected = entry;
      break;
    }
  }

  const tier = selected.tier;
  let prizeFs = 0;
  if (tier === 'fs_5_25') {
    const fsMin = intOr(params.fs_win_min, DEFAULT_FS_WIN_MIN);
    const fsMax = intOr(params.fs_win_max, DEFAULT_FS_WIN_MAX);
    prizeFs = fsMin + (parseInt(digest.slice(8, 16), 16) % (fsMax - fsMin + 1));
  }
  return { rawU, prizeTier: tier, prizeFs, forcedByPity: false };
}

function intOr(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

export type VerifyReason =
  | 'ok'
  | 'commit_mismatch'
  | 'table_hash_mismatch'
  | 'tier_mismatch'
  | 'prize_mismatch';

export interface SpinToVerify {
  nonce: number;
  mode: Mode;
  prizeTier: string;
  prizeFs: number;
  commitHash: string;
  weightTableHash: string;
  pityAtSettle: number;
}

export interface VerifyResult {
  nonce: number;
  pass: boolean;
  reason: VerifyReason;
}

// Verify one revealed spin end-to-end: the commit binds (server_seed, table); the table
// hash binds the odds; the recompute binds the outcome. All three must hold. The table's
// hash is taken over `weightTableJson` (the server's exact canonical bytes); the parsed
// `weightTable` is used only for the integer-only draw→tier mapping.
export async function verifySpin(
  spin: SpinToVerify,
  revealed: {
    serverSeedHex: string;
    clientSeed: string;
    weightTable: WeightTable;
    weightTableJson: string;
  },
): Promise<VerifyResult> {
  const expectedCommit = await commitHash(revealed.serverSeedHex, spin.weightTableHash);
  if (expectedCommit !== spin.commitHash) {
    return { nonce: spin.nonce, pass: false, reason: 'commit_mismatch' };
  }
  const tableHash = await weightTableHashFromJson(revealed.weightTableJson);
  if (tableHash !== spin.weightTableHash) {
    return { nonce: spin.nonce, pass: false, reason: 'table_hash_mismatch' };
  }
  const outcome = await resolveOutcome({
    serverSeedHex: revealed.serverSeedHex,
    clientSeed: revealed.clientSeed,
    nonce: spin.nonce,
    weightTable: revealed.weightTable,
    mode: spin.mode,
    pityAtSettle: spin.pityAtSettle,
  });
  if (outcome.prizeTier !== spin.prizeTier) {
    return { nonce: spin.nonce, pass: false, reason: 'tier_mismatch' };
  }
  if (outcome.prizeFs !== spin.prizeFs) {
    return { nonce: spin.nonce, pass: false, reason: 'prize_mismatch' };
  }
  return { nonce: spin.nonce, pass: true, reason: 'ok' };
}

export function prizeItemKey(tier: string): string | null {
  const items: Record<string, string> = {
    spin_ticket: 'spin_ticket',
    common_item: 'common_item',
    golden_ticket: 'golden_ticket',
  };
  return items[tier] ?? null;
}

export type { SpinOutcome };
